import { useCancelScheduledTransaction } from '@/hooks/requests/useTransactions';
import { SVButton, SVCheckbox, SVForm } from '../core';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  DialogActions,
} from '@mui/material';
import { useState } from 'react';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { ScheduledTransactionDetails } from '@/types/transactions';
import { useForm } from 'react-hook-form';

type ScheduledTransactionCancelButtonProps = {
  scheduledTransactionDetails: ScheduledTransactionDetails;
};

export default function ScheduledTransactionCancelButton({
  scheduledTransactionDetails,
}: ScheduledTransactionCancelButtonProps) {
  const { uuid, description, scheduleType } = scheduledTransactionDetails;

  const [open, setOpen] = useState(false);
  const formMethods = useForm({
    defaultValues: {
      accept: false,
    },
  });
  const {
    cancelScheduledTransactionPending,
    isCancelScheduledTransactionPending,
  } = useCancelScheduledTransaction();

  async function handleConfirm() {
    try {
      await cancelScheduledTransactionPending(uuid);
      showToast({
        type: 'success',
        data: { content: 'The scheduled transaction has been cancelled' },
      });
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <>
      <SVButton onClick={() => setOpen(true)} variant="error">
        Cancel
      </SVButton>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <SVForm methods={formMethods} onSubmit={handleConfirm}>
          <DialogContent>
            <Box>
              <Typography variant="h6" component="h2">
                Cancel Scheduled Transaction
              </Typography>
              <Typography sx={{ mt: 2 }}>
                Are you sure you want to cancel the{' '}
                <Typography fontWeight="bold" component="span">
                  {description}
                </Typography>{' '}
                scheduled transaction?
              </Typography>
              <SVCheckbox
                name="accept"
                label={`I understand the account owner will have to manually ${scheduleType === 'one time' ? 'recreate' : 'restart'} the scheduled transaction.`}
                rules={{ required: true }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <SVButton onClick={() => setOpen(false)} variant="tertiary">
              No, Don't Cancel
            </SVButton>
            <SVButton
              isLoading={isCancelScheduledTransactionPending}
              type="submit">
              Yes, Cancel
            </SVButton>
          </DialogActions>
        </SVForm>
      </Dialog>
    </>
  );
}
